/**
 * Soren Search Overlay.
 * Temporary workspace above the base panel.
 * Never renders inside Today.
 * Pinned composer, scrolling conversation, voice with manual send,
 * tool-labelled working state with Stop button, "Edit & ask again",
 * cash, margin, and confirm answer cards.
 */

import { useState, useRef, useEffect, useCallback, type MouseEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { CashForecastBriefing } from './SorenTodayView'
import type { PastAnswer, PastAnswerStatus } from './sorenAnswers'
import SorenCashAnswerCard from './SorenCashAnswerCard'
import SorenMarginAnswerCard from './SorenMarginAnswerCard'
import SorenConfirmAnswerCard, { type ConfirmCardData } from './SorenConfirmAnswerCard'
import SorenStatementAnswerCard from './SorenStatementAnswerCard.tsx'
import SorenWhatIfAnswerCard from './SorenWhatIfAnswerCard.tsx'
import SorenProjectFinancialsCard from './SorenProjectFinancialsCard.tsx'
import AriaChatMarkdown from '../../components/aria/AriaChatMarkdown'
import AriaChatLinkChips from '../../components/aria/AriaChatLinkChips'
import { classifySorenAnswer, safeCashReportPdfHref } from './sorenAnswerCards.js'
import { isShareHref, shouldCaptureLeavePanel } from './sorenLeavePanel.js'
import { postAriaChat, type AriaChatReply } from '../../api/ariaChat'
import { fetchDayOneChecklist } from '../../api/help'
import { fetchCashForecast, saveFridayCheckIn, type ForecastVersion } from '../../api/cashReport'
import { useActionToast } from '../../lib/useActionToast'
import { useSpeechDictation } from '../../hooks/useSpeechDictation'
import { dollarsToWords, needsMoneyWordsConfirm } from '../../lib/dollarsToWords.ts'
import { formatMoney } from '../../lib/formatters.ts'
import { classifyCashIntent } from './cashIntent.ts'
import { brand } from '../../config/brand'
import { shapeCashAnswerFromVersion } from './shapeCashAnswer.ts'
import SorenCashEngineAnswer from './SorenCashEngineAnswer.tsx'
import SorenOverdueCard from './SorenOverdueCard.tsx'
import SorenFollowUpDraftsCard from './SorenFollowUpDraftsCard.tsx'
import SorenPulseAnswerCard from './SorenPulseAnswerCard.tsx'
import {
  confirmFridayCheckIn,
  fridayAskCopy,
  fridayPrompt,
  initialFridayState,
  reduceFriday,
  type FridayState,
} from './fridayCheckIn.ts'
import { localDateKey } from '../../lib/localDate.ts'
import { writeForecastVersion } from '../cashReport/applyForecastVersion.ts'
import { track } from '../../lib/track.ts'
import { editDistance } from '../../lib/editDistance.ts'

interface MessageItem {
  id: string
  role: 'user' | 'assistant'
  question?: string
  content?: string
  toolActivity?: string
  answerType?: 'cash' | 'margin' | 'confirm' | 'payments' | 'general'
  confirmData?: ConfirmCardData
  isConfirmed?: boolean
  isCancelled?: boolean
  shaped?: import('./shapeCashAnswer.ts').ShapedCashAnswer
  version?: ForecastVersion
  preparedText?: string | null
  cardKind?: 'forecast' | 'statement' | 'what_if' | 'cash_report_pdf' | 'overdue' | 'followup' | 'pulse' | null
  digest?: AriaChatReply['digest']
  links?: AriaChatReply['links']
}

interface SorenSearchOverlayProps {
  open: boolean
  autoRecord?: boolean
  initialType?: string | null
  briefing: CashForecastBriefing | null
  onClose: () => void
  onSavePastAnswer: (payload: {
    question: string
    oneLineResult: string
    type?: PastAnswer['type']
    replyData?: AriaChatReply | null
    awaitingConfirmation?: boolean
    toolNames?: string[]
    projectId?: number | null
    answerJson?: Record<string, unknown>
  }) => PastAnswer
  onUpdateConfirmation: (id: string, awaitingOrStatus: boolean | PastAnswerStatus) => void
  pageVersionId?: string | null
  onOpenCashReport?: () => void
  onOpenSource?: (href: string) => void
}

const DEFAULT_SUGGESTIONS = [
  'Why is Sep 18 short?',
  "What's my margin?",
  'What can I bill now?',
  'Which Taj invoices are overdue?',
]

export default function SorenSearchOverlay({
  open,
  autoRecord = false,
  initialType = null,
  briefing,
  onClose,
  onSavePastAnswer,
  onUpdateConfirmation,
  pageVersionId = null,
  onOpenCashReport,
  onOpenSource,
}: SorenSearchOverlayProps) {
  const { showToast, Toast } = useActionToast()
  const queryClient = useQueryClient()
  const location = useLocation()
  const helpStateQ = useQuery({
    queryKey: ['help-day-one'],
    queryFn: fetchDayOneChecklist,
    staleTime: 60_000,
  })
  const [messages, setMessages] = useState<MessageItem[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recordTimer, setRecordTimer] = useState('0:00')
  const [voiceNotice, setVoiceNotice] = useState<{ status: string; help: string } | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  const [workingLabel, setWorkingLabel] = useState('checking Pay Apps, costs, and commitments')
  const [isStopped, setIsStopped] = useState(false)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const conversationRef = useRef<HTMLDivElement | null>(null)
  const recordIntervalRef = useRef<number | null>(null)
  const activeAbortRef = useRef<AbortController | null>(null)
  const lastPastAnswerIdRef = useRef<string | null>(null)
  const ignoreTranscriptRef = useRef(false)

  const leavePanel = useCallback((href: string) => {
    onOpenSource?.(href)
  }, [onOpenSource])

  function onLeavePanelCapture(e: MouseEvent) {
    if (!onOpenSource) return
    const a = (e.target as HTMLElement).closest('a[href]')
    if (!a) return
    const href = a.getAttribute('href') || ''
    if (isShareHref(href)) return
    if (!shouldCaptureLeavePanel(href)) return
    e.preventDefault()
    e.stopPropagation()
    leavePanel(href)
  }
  const lastTranscriptRef = useRef<string | null>(null)
  const inputValueRef = useRef('')
  inputValueRef.current = inputValue
  const [pageVid, setPageVid] = useState<string | null>(pageVersionId)
  const [friday, setFriday] = useState<FridayState>(() => initialFridayState())
  const fridayRef = useRef(friday)
  fridayRef.current = friday
  const speechRef = useRef<ReturnType<typeof useSpeechDictation> | null>(null)

  const speech = useSpeechDictation({
    onFinalText: (text) => {
      if (ignoreTranscriptRef.current) return
      lastTranscriptRef.current = text
      setInputValue(text)
      const dollars = Number(String(text).replace(/[^0-9.]/g, ''))
      if (needsMoneyWordsConfirm(dollars)) {
        setVoiceNotice({
          status: 'Transcript ready · not sent',
          help: `Readback: ${dollarsToWords(dollars)}. Edit, then press Send.`,
        })
      } else {
        setVoiceNotice({
          status: 'Transcript ready · not sent',
          help: 'Read and edit the words above, then press Send or Enter.',
        })
      }
    },
    onInterim: (text) => {
      if (text && text !== 'Transcribing…') setInputValue(text)
    },
    onListeningChange: () => {},
  })
  speechRef.current = speech

  const voicePlaceholder = speech.transcribing
    ? 'Transcribing…'
    : speech.listening
      ? 'Listening…'
      : 'Ask about Pay Apps, Subs, costs, cash…'
  const voiceBusy = speech.transcribing || speech.listening || isRecording
  const voiceBusyLabel = speech.transcribing
    ? 'Transcribing…'
    : speech.listening
      ? 'Listening…'
      : `Recording ${recordTimer}`

  // Stop recording helper
  const stopRecording = useCallback((showReview = true) => {
    if (recordIntervalRef.current) {
      window.clearInterval(recordIntervalRef.current)
      recordIntervalRef.current = null
    }
    setIsRecording(false)
    if (showReview) {
      setVoiceNotice({
        status: 'Recording stopped · transcript ready',
        help: 'Read and edit the words above, then press Send or Enter.',
      })
    } else {
      setVoiceNotice(null)
    }
  }, [])

  // Start recording helper
  const startRecording = useCallback(() => {
    ignoreTranscriptRef.current = false
    setIsRecording(true)
    setRecordTimer('0:00')
    setVoiceNotice({
      status: 'Recording · 0:00',
      help: 'Speak naturally. Tap the square to stop.',
    })
    inputRef.current?.focus()

    let secCount = 0
    recordIntervalRef.current = window.setInterval(() => {
      secCount += 1
      const mins = Math.floor(secCount / 60)
      const secs = String(secCount % 60).padStart(2, '0')
      const formatted = `${mins}:${secs}`
      setRecordTimer(formatted)
      setVoiceNotice({
        status: `Recording · ${formatted}`,
        help: 'Speak naturally. Tap the square to stop.',
      })
    }, 1000)

  }, [])

  useEffect(() => {
    if (pageVersionId) setPageVid(pageVersionId)
  }, [pageVersionId])

  // Initial mount / open handling
  useEffect(() => {
    if (!open) {
      stopRecording(false)
      speechRef.current?.clearAll()
      setIsWorking(false)
      setIsStopped(false)
      setFriday(initialFridayState())
      setMessages([])
      return
    }

    let cancelled = false
    window.setTimeout(() => { if (!cancelled) inputRef.current?.focus() }, 60)

    if (initialType === 'friday') {
      void fetchCashForecast().then((res) => {
        if (cancelled) return
        const stale = !!res.data?.version?.payload?.bank_stale
        const next = reduceFriday(initialFridayState(), { type: 'start', bankStale: stale })
        setFriday(next)
        setMessages([{
          id: 'a_fri',
          role: 'assistant',
          content: fridayAskCopy(next),
        }])
      }).catch(() => {})
      return () => { cancelled = true }
    }

    if (initialType === 'cash') {
      setMessages([])
      const t = autoRecord
        ? window.setTimeout(() => { if (!cancelled) speechRef.current?.toggle('') }, 80)
        : 0
      return () => {
        cancelled = true
        if (t) window.clearTimeout(t)
      }
    } else if (initialType === 'margin') {
      const q = "What's my margin on Roof Slope?"
      setMessages([
        { id: 'u_init', role: 'user', question: q },
        {
          id: 'a_init',
          role: 'assistant',
          toolActivity: 'checked entered costs and commitments',
          answerType: 'margin',
        },
      ])
      const ans = onSavePastAnswer({
        question: q,
        type: 'Margin',
        oneLineResult: 'Projected margin based on entered costs and commitments.',
        toolNames: ['margin_calculator', 'sov_lines', 'commitments'],
        answerJson: {
          kind: 'margin_analysis',
          figures: {
            contract_value: 51600.0,
            actual_costs: 31742.0,
            projected_margin: 19858.0,
            margin_pct: 38.5,
          },
          project_name: 'Roof Slope',
        },
      })
      lastPastAnswerIdRef.current = ans.id
    } else if (initialType === 'confirm') {
      const q = 'Add the lift rental due Sep 28'
      const confirmData: ConfirmCardData = {
        title: 'Save cost',
        subtitle: 'Check the fields. Soren cannot add this to the project until you approve it.',
        ruleNotice: 'This unpaid cost will flow into the Sep 28 cash forecast.',
        successMessage: 'Confirmed. Cost added to Roof Slope and the cash forecast.',
        fields: [
          { label: 'Description', value: 'Lift rental, 2 days', wide: true },
          { label: 'Category', value: 'Equipment' },
          { label: 'Amount', value: '$0.00', isMoney: true },
          { label: 'Cost date', value: '2026-09-11' },
          { label: 'Due date · unpaid', value: '2026-09-28' },
        ],
        onConfirm: () => {
          showToast('Saved only after your confirmation')
          if (lastPastAnswerIdRef.current) {
            onUpdateConfirmation(lastPastAnswerIdRef.current, false)
          }
        },
        onCancel: () => {
          showToast('Cancelled — nothing was saved')
        },
      }
      setMessages([
        { id: 'u_init', role: 'user', question: q },
        {
          id: 'a_init',
          role: 'assistant',
          toolActivity: 'ready for your approval',
          answerType: 'confirm',
          confirmData,
        },
      ])
      const ans = onSavePastAnswer({
        question: q,
        type: 'Cost',
        oneLineResult: 'Soren prepared the cost but did not save it because Confirm was not tapped.',
        awaitingConfirmation: true,
        toolNames: ['prepare_cost_entry'],
        answerJson: {
          kind: 'confirm_card',
          card_data: {
            title: confirmData.title,
            subtitle: confirmData.subtitle,
            rule_notice: confirmData.ruleNotice,
            success_message: confirmData.successMessage,
            action_payload: {
              action: 'create_cost',
              cost_id: null,
              project_name: 'Roof Slope',
              category: 'Equipment',
              amount: 660.0,
              cost_date: '2026-09-11',
              due_date: '2026-09-28',
              description: 'Lift rental, 2 days',
            },
            fields: confirmData.fields,
          },
        },
      })
      lastPastAnswerIdRef.current = ans.id
    } else if (autoRecord) {
      window.setTimeout(() => { if (!cancelled) speechRef.current?.toggle('') }, 120)
    }
  }, [open, initialType, autoRecord, briefing, onSavePastAnswer, startRecording, stopRecording])

  // Scroll down on messages
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight
    }
  }, [messages, isWorking, isStopped])

  async function confirmFriday() {
    const started = reduceFriday(fridayRef.current, { type: 'confirm' })
    setFriday(started)
    try {
      const out = await confirmFridayCheckIn(started, {
        save: saveFridayCheckIn,
        writeVersion: (version, extras) => writeForecastVersion(queryClient, version, 1, extras),
        dateKey: () => localDateKey(),
      })
      setFriday(out.state)
      const version = out.version
      if (version) {
        const shaped = shapeCashAnswerFromVersion('Am I short this week?', version as ForecastVersion)
        setMessages((prev) => [...prev, {
          id: `a_fri_done`,
          role: 'assistant',
          answerType: 'cash',
          cardKind: 'forecast',
          content: `Confirmed. This week ${shaped.amount}. Lowest uses version ${shaped.version_id.slice(0, 8)}.`,
          shaped,
          version: version as ForecastVersion,
        }])
      }
      showToast('Cash Report updated after confirmation')
    } catch {
      setFriday(reduceFriday(started, { type: 'fail' }))
      showToast('Friday check-in could not save')
    }
  }

  // Send question handler
  async function handleSend(textToSend?: string) {
    // Flush speech BEFORE ignoreTranscriptRef — setting the flag first dropped
    // onFinalText and Send could post "" / "Transcribing…".
    if (isRecording) stopRecording(false)
    let flushed = ''
    if (speech.listening || speech.transcribing) {
      flushed = String(await speech.flushAndStop() || '').trim()
    }
    ignoreTranscriptRef.current = true
    const q = (textToSend ?? (flushed || inputValueRef.current || inputValue)).trim()
    if (!q || q === 'Transcribing…') {
      showToast('Ask Soren a question')
      return
    }

    const original = lastTranscriptRef.current
    if (original != null && original !== q) {
      track('soren_transcript_edited', {
        chars_before: original.length,
        chars_after: q.length,
        edit_distance: editDistance(original, q),
      })
    }
    lastTranscriptRef.current = null

    setVoiceNotice(null)
    setInputValue('')
    setIsStopped(false)

    const intent = classifyCashIntent(q)
    if (intent === 'navigate') {
      setMessages((prev) => [...prev, { id: `u_${Date.now()}`, role: 'user', question: q }])
      onSavePastAnswer({
        question: q,
        type: 'Cash',
        oneLineResult: 'Opened Company Cash Report.',
        toolNames: [],
        answerJson: { kind: 'navigate_cash_report' },
      })
      onOpenCashReport?.()
      return
    }
    if (intent === 'ambiguous') {
      setMessages((prev) => [
        ...prev,
        { id: `u_${Date.now()}`, role: 'user', question: q },
        {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: 'Do you want to open the Company Cash Report, or ask a question about this week’s cash?',
        },
      ])
      return
    }
    const fridayActive = friday.phase === 'asking' || friday.phase === 'review' || friday.phase === 'confirming'
    if (intent === 'friday' || fridayActive) {
      // friday handled below after the user bubble
    }

    // User question bubble
    const userMsgId = `u_${Date.now()}`
    setMessages((prev) => [...prev, { id: userMsgId, role: 'user', question: q }])

    if (intent === 'friday' && !fridayActive) {
      let stale = false
      try {
        const fc = await fetchCashForecast()
        stale = !!fc.data?.version?.payload?.bank_stale
      } catch { /* estimates-only path */ }
      const next = reduceFriday(friday, { type: 'start', bankStale: stale })
      setFriday(next)
      setMessages((prev) => [...prev, {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: fridayAskCopy(next),
      }])
      return
    }

    if (friday.phase === 'review' || friday.phase === 'confirming') {
      showToast('Confirm or cancel the Friday check-in first')
      return
    }

    if (friday.phase === 'asking') {
      const next = reduceFriday(friday, { type: 'answer', text: q })
      setFriday(next)
      if (next.parseError) {
        setMessages((prev) => [...prev, {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: 'Estimates are fine — send an amount or say zero.',
        }])
        return
      }
      const nextAnswers = next.answers
      if (next.phase === 'review') {
        setMessages((prev) => [...prev, {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: `${fridayPrompt('review')}\nPayroll ${formatMoney((nextAnswers.payroll_cents || 0) / 100)}\nBills ${formatMoney((nextAnswers.bills_cents || 0) / 100)}\nUnusual ${formatMoney((nextAnswers.unusual_cents || 0) / 100)}`,
          confirmData: {
            title: 'Confirm and recalculate',
            subtitle: 'Writes Friday check-in assumptions.',
            ruleNotice: 'Nothing is saved until you confirm.',
            fields: [
              { label: 'Payroll', value: formatMoney((nextAnswers.payroll_cents || 0) / 100) },
              { label: 'Bills', value: formatMoney((nextAnswers.bills_cents || 0) / 100) },
              { label: 'Unusual', value: formatMoney((nextAnswers.unusual_cents || 0) / 100) },
              ...(nextAnswers.cash_on_hand_cents != null
                ? [{ label: 'Cash on hand', value: formatMoney(nextAnswers.cash_on_hand_cents / 100) }]
                : []),
            ],
            onConfirm: () => { void confirmFriday() },
            onCancel: () => {
              setFriday(reduceFriday(fridayRef.current, { type: 'cancel' }))
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.answerType === 'confirm' ? { ...msg, isCancelled: true } : msg,
                ),
              )
              showToast('Cancelled — nothing was saved')
            },
          },
          answerType: 'confirm',
        }])
      } else {
        setMessages((prev) => [...prev, {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: fridayAskCopy(next),
        }])
      }
      return
    }

    // Working state
    setIsWorking(true)
    const lower = q.toLowerCase()
    const activity = lower.includes('cash') || lower.includes('runway')
      ? 'checked cash forecast'
      : lower.includes('margin')
        ? 'checked margin'
        : lower.includes('cost') || lower.includes('lift') || lower.includes('rental')
          ? 'checked job costs and commitments'
          : lower.includes('pay app') || lower.includes('overdue')
            ? 'checked Pay Apps and owner timing'
            : 'checking Pay Apps, costs, and commitments'
    setWorkingLabel(activity)

    const controller = new AbortController()
    activeAbortRef.current = controller

    try {
      // Call live chat API
      const hs = helpStateQ.data?.state
      const reply = await postAriaChat(
        [{ role: 'user', content: q }],
        {
          route: `${location.pathname}${location.search}`,
          has_logo: hs?.has_logo,
          has_signature: hs?.has_signature,
          project_count: hs?.project_count,
          pa_count: hs?.pa_count,
        },
      )
      if (controller.signal.aborted) return

      setIsWorking(false)
      const assistantMsgId = `a_${Date.now()}`
      let shaped: MessageItem['shaped']
      let version: ForecastVersion | undefined
      const cardKind = classifySorenAnswer(q, reply.digest)
      const cashQ = intent === 'question' && cardKind === 'forecast'
      if (cashQ) {
        try {
          const fc = await fetchCashForecast()
          version = fc.data?.version ?? undefined
          if (version) {
            setPageVid(version.id)
            shaped = shapeCashAnswerFromVersion(q, version, fc.data?.actions)
          }
        } catch { /* overlay still shows chat prose */ }
      }
      const hasAction = Array.isArray(reply.actions) && reply.actions.length > 0
      const isCashQuery = cashQ
      const isMarginQuery = lower.includes('margin')

      let answerType: MessageItem['answerType'] = 'general'
      let confirmData: ConfirmCardData | undefined

      if (isCashQuery) {
        answerType = 'cash'
      } else if (isMarginQuery) {
        answerType = 'margin'
      } else if (hasAction && reply.actions) {
        answerType = 'confirm'
        const act = reply.actions[0]
        const actionLabel = act?.action_type
          ? act.action_type.replace(/_/g, ' ')
          : 'Save action'
        confirmData = {
          title: actionLabel,
          subtitle: 'Check the fields. Soren cannot execute this until you approve it.',
          ruleNotice: 'Nothing is saved until you tap Confirm.',
          fields: [
            { label: 'Action', value: actionLabel, wide: true },
            { label: 'Status', value: act?.status || 'Prepared' },
          ],
          onConfirm: () => {
            showToast('Saved only after your confirmation')
            if (lastPastAnswerIdRef.current) {
              onUpdateConfirmation(lastPastAnswerIdRef.current, false)
            }
          },
          onCancel: () => {
            showToast('Cancelled — nothing was saved')
          },
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMsgId,
          role: 'assistant',
          toolActivity: reply.tool_activity?.join(', ') || activity,
          content: reply.content,
          answerType,
          confirmData,
          shaped,
          version,
          cardKind,
          digest: reply.digest,
          links: reply.links,
          preparedText: typeof (reply as { draft?: string }).draft === 'string'
            ? (reply as { draft?: string }).draft
            : null,
        },
      ])

      const isCash = answerType === 'cash'
      const isMargin = answerType === 'margin'
      const isConfirm = answerType === 'confirm'

      const answerJsonPayload: Record<string, unknown> = {}
      if (isCash) {
        answerJsonPayload.kind = 'cash_forecast'
        if (shaped && version) {
          answerJsonPayload.figures = {
            amount: shaped.amount,
            date: shaped.date,
            reason: shaped.reason,
            confidence: shaped.confidence,
          }
          answerJsonPayload.verdict = shaped.text
        } else {
          answerJsonPayload.figures = {
            bank_balance: briefing?.bankAmount ?? null,
            lowest_point: briefing?.lowestPointAmount ?? null,
            lowest_week: briefing?.lowestWeek ?? null,
            expected_in_30d: briefing?.expectedInAmount ?? null,
          }
          answerJsonPayload.verdict = briefing?.verdictLine ?? 'Cash forecast updated.'
        }
      } else if (isMargin) {
        answerJsonPayload.kind = 'margin_analysis'
        answerJsonPayload.figures = {
          contract_value: 51600.0,
          actual_costs: 31742.0,
          projected_margin: 19858.0,
          margin_pct: 38.5,
        }
      } else if (isConfirm && confirmData) {
        answerJsonPayload.kind = 'confirm_card'
        answerJsonPayload.card_data = {
          title: confirmData.title,
          subtitle: confirmData.subtitle,
          rule_notice: confirmData.ruleNotice,
          fields: confirmData.fields,
        }
      } else {
        answerJsonPayload.kind = 'assistant_reply'
        answerJsonPayload.content = reply.content
      }

      const chatWillPersist = !!(
        reply.digest
        || (Array.isArray(reply.tool_activity) && reply.tool_activity.length)
        || (reply as { tool_calls_used?: boolean }).tool_calls_used
      )
      if (isConfirm) {
        const saved = onSavePastAnswer({
          question: q,
          oneLineResult: reply.content.slice(0, 140) || 'Answer ready in Past answers.',
          type: 'Cost',
          replyData: reply,
          awaitingConfirmation: true,
          toolNames: reply.tool_activity || ['assistant_chat'],
          answerJson: answerJsonPayload,
        })
        lastPastAnswerIdRef.current = saved.id
      } else if (isCash || !chatWillPersist) {
        onSavePastAnswer({
          question: q,
          oneLineResult: (shaped?.text || reply.content).slice(0, 140),
          type: isCash ? 'Cash' : isMargin ? 'Margin' : 'Answer',
          replyData: reply,
          toolNames: reply.tool_activity || ['assistant_chat'],
          answerJson: { ...answerJsonPayload, version_id: shaped?.version_id, figures_text: shaped?.text },
        })
      }
      void queryClient.invalidateQueries({ queryKey: ['soren-answers'] })
      showToast('Answer saved in Past answers')
    } catch {
      if (controller.signal.aborted) return
      setIsWorking(false)
      try {
        const fc = await fetchCashForecast()
        const version = fc.data?.version
        if (version && classifySorenAnswer(q, null) === 'forecast') {
          const shaped = shapeCashAnswerFromVersion(q, version, fc.data?.actions)
          setPageVid(version.id)
          setMessages((prev) => [...prev, {
            id: `a_${Date.now()}`,
            role: 'assistant',
            toolActivity: 'checking cash forecast',
            answerType: 'cash',
            cardKind: 'forecast',
            shaped,
            version,
          }])
          onSavePastAnswer({
            question: q,
            type: 'Cash',
            oneLineResult: shaped.text.slice(0, 140),
            toolNames: ['get_cash_forecast'],
            answerJson: { kind: 'cash_forecast', version_id: version.id, figures_text: shaped.text },
          })
          return
        }
      } catch { /* fall through */ }

      // Fallback display if offline or API error
      const isCashQuery = lower.includes('cash') || lower.includes('runway')
      const isMarginQuery = lower.includes('margin')
      const isCostQuery = lower.includes('cost') || lower.includes('lift') || lower.includes('rental')

      let answerType: MessageItem['answerType'] = 'general'
      let confirmData: ConfirmCardData | undefined

      if (isCashQuery) {
        answerType = 'cash'
      } else if (isMarginQuery) {
        answerType = 'margin'
      } else if (isCostQuery) {
        answerType = 'confirm'
        confirmData = {
          title: 'Save cost',
          subtitle: 'Check the fields. Soren cannot add this to the project until you approve it.',
          ruleNotice: 'This unpaid cost will flow into the cash forecast.',
          fields: [
            { label: 'Description', value: 'Rental equipment', wide: true },
            { label: 'Category', value: 'Equipment' },
            { label: 'Amount', value: '$0.00', isMoney: true },
          ],
          onConfirm: () => {
            showToast('Saved only after your confirmation')
            if (lastPastAnswerIdRef.current) {
              onUpdateConfirmation(lastPastAnswerIdRef.current, false)
            }
          },
          onCancel: () => {
            showToast('Cancelled — nothing was saved')
          },
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `a_${Date.now()}`,
          role: 'assistant',
          toolActivity: activity,
          content: `I checked current ${brand.name} data. Detailed figures are shown below.`,
          answerType,
          confirmData,
        },
      ])
      // Error/offline fallback: do not persist (no fabricated or demand-log rows).
    } finally {
      activeAbortRef.current = null
    }
  }

  // Stop button handler
  function handleStop(lastQuestion?: string) {
    if (activeAbortRef.current) {
      activeAbortRef.current.abort()
      activeAbortRef.current = null
    }
    setIsWorking(false)
    setIsStopped(true)
    if (lastQuestion) {
      setInputValue(lastQuestion)
    }
    inputRef.current?.focus()
    showToast('Soren stopped')
  }

  // Edit question handler
  function handleEditQuestion(q: string) {
    setInputValue(q)
    inputRef.current?.focus()
    setVoiceNotice({
      status: 'Question ready to edit',
      help: 'Correct any words, then press Send or Enter.',
    })
  }

  if (!open) return null

  return (
    <section
      className="search-overlay show"
      id="searchOverlay"
      data-testid="soren-search-overlay"
      aria-label="Ask Soren overlay"
      aria-modal="true"
      role="dialog"
      onClickCapture={onLeavePanelCapture}
    >
      <header className="overlay-head">
        <span className="s-mark">S</span>
        <div className="overlay-head-copy">
          <b>Ask Soren</b>
          <span>Temporary workspace · answers are saved in Past answers</span>
        </div>
        <kbd className="esc-hint">Esc</kbd>
        <button
          className="close-btn"
          id="closeSearch"
          data-testid="soren-search-overlay-close"
          type="button"
          aria-label="Close search and return to Today"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <div className="overlay-search">
        <form
          className="search-box"
          id="askForm"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSend()
          }}
        >
          <input
            ref={inputRef}
            id="askInput"
            data-testid="soren-overlay-input"
            autoComplete="off"
            placeholder={voicePlaceholder}
            aria-label="Question for Soren"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button
            className={`mic ${voiceBusy ? 'recording' : ''}`}
            id="voiceButton"
            data-testid="soren-voice-button"
            type="button"
            aria-label={speech.transcribing ? 'Transcribing' : speech.listening ? 'Stop listening' : 'Start voice recording'}
            aria-pressed={voiceBusy}
            onClick={() => {
              ignoreTranscriptRef.current = false
              speech.toggle(inputValue)
            }}
          >
            {voiceBusy ? (
              <>
                <span className="stop-square" aria-hidden="true" />
                <span className="rec-text" data-testid="soren-voice-state" aria-hidden="true">
                  {voiceBusyLabel}
                </span>
              </>
            ) : (
              <span className="mic-glyph" aria-hidden="true" />
            )}
          </button>
          <button
            className="ask"
            type="submit"
            data-testid="soren-overlay-send"
          >
            Send
          </button>
        </form>

        {voiceNotice && (
          <div className={`voice-state show ${!isRecording ? 'review' : ''}`} role="status">
            {isRecording && <span className="record-dot" aria-hidden="true" />}
            <span className="voice-copy">
              <b>{voiceNotice.status}</b>
              <span>{voiceNotice.help}</span>
            </span>
          </div>
        )}

        {messages.length === 0 && !isRecording && (
          <div className="suggestions" id="suggestions">
            {DEFAULT_SUGGESTIONS.map((s) => (
              <button
                key={s}
                className="suggestion"
                type="button"
                onClick={() => {
                  setInputValue(s)
                  void handleSend(s)
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="conversation" id="conversation" ref={conversationRef}>
        {messages.length === 0 && (
          <div className="conversation-empty" id="conversationEmpty">
            <b>Ask one thing at a time.</b>
            <span>
              Soren will show the answer here. Close with Escape or × whenever you are done; the daily cash briefing stays underneath.
            </span>
          </div>
        )}

        <div id="conversationMessages">
          {messages.map((m) => {
            if (m.role === 'user') {
              return (
                <div key={m.id} className="message user">
                  <div className="question-wrap">
                    <div className="user-bubble">{m.question}</div>
                    <button
                      className="edit-question"
                      data-testid="soren-edit-question"
                      type="button"
                      onClick={() => handleEditQuestion(m.question || '')}
                    >
                      Edit &amp; ask again
                    </button>
                  </div>
                </div>
              )
            }

            return (
              <div key={m.id} className="message assistant">
                <div className="assistant-label">
                  <span className="mini-s">S</span>
                  Soren · {m.toolActivity || `checked current ${brand.name} data`}
                </div>
                {m.links?.length ? (
                  <AriaChatLinkChips links={m.links} onInternalNav={leavePanel} />
                ) : null}

                {m.content && m.cardKind !== 'forecast' && m.cardKind !== 'overdue' && m.cardKind !== 'followup' && !(m.cardKind === 'what_if' && m.digest?.kind === 'what_if') && (
                  <div className="prose" data-testid="soren-answer-prose">
                    <AriaChatMarkdown text={m.content} chipClassName="suggestion" onInternalNav={leavePanel} />
                  </div>
                )}

                {m.digest?.kind === 'project_financials' && (
                  <SorenProjectFinancialsCard digest={m.digest} />
                )}

                {m.digest?.kind === 'cash_report_pdf' && safeCashReportPdfHref(String((m.digest as { download_url?: string }).download_url || '')) && (
                  <a
                    className="cr-btn dark"
                    data-testid="soren-cash-report-download"
                    href={safeCashReportPdfHref(String((m.digest as { download_url?: string }).download_url || '')) || '#'}
                  >
                    Download PDF
                  </a>
                )}

                {((m.digest as { kind?: string } | undefined)?.kind === 'pulse') && (m.digest as { href?: string }).href && (
                  <SorenPulseAnswerCard
                    href={String((m.digest as { href: string }).href)}
                    label={m.links?.[0]?.label}
                    onOpen={(href) => onOpenSource?.(href)}
                  />
                )}

                {m.cardKind === 'statement' && m.digest?.kind === 'owner_statement' && (
                  <SorenStatementAnswerCard
                    digest={m.digest as import('./SorenStatementAnswerCard').StatementDigest}
                    onOpenReport={(href) => onOpenSource?.(href)}
                  />
                )}

                {m.cardKind === 'what_if' && m.digest?.kind === 'what_if' && (
                  <SorenWhatIfAnswerCard digest={m.digest as import('./SorenWhatIfAnswerCard').WhatIfDigest} />
                )}

                {m.cardKind === 'overdue' && m.digest?.kind === 'overdue_collections' && (
                  <SorenOverdueCard
                    digest={m.digest as import('./SorenOverdueCard').OverdueDigest}
                    onDraftFollowups={() => { void handleSend('Draft follow-ups') }}
                  />
                )}

                {m.cardKind === 'followup' && m.digest?.kind === 'overdue_followup_drafts' && (
                  <SorenFollowUpDraftsCard
                    digest={m.digest as import('./SorenFollowUpDraftsCard').FollowUpDigest}
                  />
                )}

                {m.cardKind === 'forecast' && m.shaped && m.version && (
                  <SorenCashEngineAnswer
                    shaped={m.shaped}
                    version={m.version}
                    pageVersionId={pageVid}
                    preparedText={m.preparedText}
                    onOpenReport={() => onOpenCashReport?.()}
                    onRefresh={() => { void fetchCashForecast().then((r) => { if (r.data?.version) setPageVid(r.data.version.id) }) }}
                    onOpenSource={(href) => onOpenSource?.(href)}
                  />
                )}

                {m.cardKind === 'forecast' && !m.shaped && (
                  <SorenCashAnswerCard briefing={briefing} />
                )}

                {m.answerType === 'margin' && (
                  <SorenMarginAnswerCard />
                )}

                {m.answerType === 'confirm' && m.confirmData && (
                  <SorenConfirmAnswerCard
                    data={m.confirmData}
                    isConfirmed={m.isConfirmed}
                    isCancelled={m.isCancelled}
                    onConfirmedChange={(conf) => {
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === m.id ? { ...msg, isConfirmed: conf } : msg,
                        ),
                      )
                    }}
                  />
                )}
              </div>
            )
          })}

          {isWorking && (
            <div className="message assistant">
              <div
                className="working"
                data-testid="soren-working-state"
                data-activity={workingLabel}
              >
                <span className="dots" data-testid="soren-working-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="working-copy" data-testid="soren-working-label">
                  <b>Working</b> · {workingLabel}
                </span>
                <button
                  className="stop-working"
                  data-testid="soren-stop-btn"
                  type="button"
                  onClick={() => {
                    const lastUserQ = [...messages].reverse().find((x) => x.role === 'user')?.question
                    handleStop(lastUserQ)
                  }}
                >
                  Stop
                </button>
              </div>
            </div>
          )}

          {isStopped && (
            <div className="message assistant">
              <div className="working stopped">
                <span className="working-copy">
                  <b>Stopped</b> · edit the question above or send it again
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="overlay-footer">
        Press Escape or × to return to Today. Completed answers are kept in Past answers.
      </footer>
      {Toast}
    </section>
  )
}
